import { getGetSubscriptionQueryKey, getSubscription } from '@modusign/openapi-client-subscription';
import { ErrorType } from '@modusign/openapi-client-subscription/custom-client';
import { GetSubscriptionResponseDto } from '@modusign/openapi-client-subscription/model';
import { useQuery, UseQueryOptions } from 'react-query';
import { Merge } from 'ts-essentials';

import Plan from '@app/features/subscription/types/Plan';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';

export type SubscriptionPlan =
  | (Omit<Plan, 'permissions' | 'limits' | 'fees' | 'reasons'> & { isLegacy: boolean })
  | null;
export type Subscription = Merge<
  GetSubscriptionResponseDto,
  { plan: SubscriptionPlan; nextPlan: SubscriptionPlan }
>;

export function useSubscription<TError = ErrorType<Error>>(
  options?: Omit<
    UseQueryOptions<
      Awaited<ReturnType<typeof getSubscription>>,
      TError,
      Subscription,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any
    >,
    'queryKey' | 'queryFn' | 'select'
  >
) {
  const workspaceId = useWorkspaceId();
  return useQuery({
    queryKey: getGetSubscriptionQueryKey(workspaceId),
    queryFn: () => getSubscription(workspaceId),
    select: (subscription) => ({
      ...subscription,
      plan: (subscription.plan && {
        ...subscription.plan,
        isLegacy: subscription.plan?.planVersion !== 1,
      }) as SubscriptionPlan,
      nextPlan: (subscription.nextPlan && {
        ...subscription.nextPlan,
        isLegacy: subscription.nextPlan?.planVersion !== 1,
      }) as SubscriptionPlan,
    }),
    staleTime: 1000 * 60 * 5,
    ...options,
  });
}
